/**
 * @module ui/ui_human-urls
 * @description
 * Модуль для работы с человекочитаемыми URL фильтров коллекций
 */

import * as HumanReadableUrlsHandlers from '@/ui/ui_human-urls/handlers';

// Автоматическая инициализация обработчиков при загрузке страницы
// Инициализация происходит только если фича включена и window.Shop.config готов
if (typeof document !== 'undefined') {
  const initHandlers = () => {
    // Проверяем готовность window.Shop.config перед инициализацией
    if (window.Shop && window.Shop.config && HumanReadableUrlsHandlers.isHumanReadableEnabled()) {
      HumanReadableUrlsHandlers.setupFilterWidgetHandlers();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHandlers);
  } else {
    // Если DOM уже загружен, проверяем готовность config с небольшой задержкой
    // на случай, если config еще не инициализирован
    if (window.Shop && window.Shop.config) {
      initHandlers();
    } else {
      // Пытаемся инициализировать после небольшой задержки
      setTimeout(initHandlers, 100);
    }
  }
}

/**
 * Создаёт мапы id → permalink для свойств, характеристик и опций
 */
export function buildPermalinkMaps(json) {
  const propertyIdToPermalink = {};
  const characteristicIdToPermalink = {};
  const optionNameIdToPermalink = {};
  const optionValueIdToPermalink = {};

  if (!json) {
    return {
      propertyIdToPermalink,
      characteristicIdToPermalink,
      optionNameIdToPermalink,
      optionValueIdToPermalink
    };
  }

  if (Array.isArray(json.properties)) {
    json.properties.forEach((prop) => {
      if (prop && prop.id && prop.permalink) {
        propertyIdToPermalink[prop.id] = prop.permalink;
      }

      if (Array.isArray(prop && prop.characteristics)) {
        prop.characteristics.forEach((char) => {
          if (char && char.id && char.permalink) {
            characteristicIdToPermalink[char.id] = char.permalink;
          }
        });
      }
    });
  }

  if (Array.isArray(json.options)) {
    json.options.forEach((opt) => {
      if (opt && opt.id && opt.permalink) {
        optionNameIdToPermalink[opt.id] = opt.permalink;
      }

      if (Array.isArray(opt && opt.values)) {
        opt.values.forEach((value) => {
          if (value && value.id && value.permalink) {
            optionValueIdToPermalink[value.id] = value.permalink;
          }
        });
      }
    });
  }

  return {
    propertyIdToPermalink,
    characteristicIdToPermalink,
    optionNameIdToPermalink,
    optionValueIdToPermalink
  };
}

/**
 * Парсит человекочитаемые фильтры из URL
 */
function parseHumanReadableFiltersFromUrl(json) {
  const searchParams = new URLSearchParams(window.location.search);
  const selectedCharacteristicIds = [];
  const selectedOptionValueIdsByOptionNameId = {};

  if (!json) {
    return { selectedCharacteristicIds, selectedOptionValueIdsByOptionNameId };
  }

  const systemKeys = new Set(['price_min', 'price_max', 'order', 'page']);
  const propertyPermalinkToId = {};
  const characteristicPermalinkToInfo = {};

  if (Array.isArray(json.properties)) {
    json.properties.forEach((prop) => {
      if (prop && prop.id && prop.permalink) {
        propertyPermalinkToId[prop.permalink] = prop.id;

        if (Array.isArray(prop.characteristics)) {
          prop.characteristics.forEach((char) => {
            if (char && char.id && char.permalink) {
              characteristicPermalinkToInfo[char.permalink] = {
                id: char.id,
                propertyId: prop.id
              };
            }
          });
        }
      }
    });
  }

  const optionPermalinkToId = {};
  const optionValuePermalinkToInfo = {};

  if (Array.isArray(json.options)) {
    json.options.forEach((opt) => {
      if (opt && opt.id && opt.permalink) {
        optionPermalinkToId[opt.permalink] = opt.id;

        if (Array.isArray(opt.values)) {
          opt.values.forEach((value) => {
            if (value && value.id && value.permalink) {
              // Используем составной ключ: optionPermalink/valuePermalink
              // Это предотвращает конфликты когда разные опции имеют одинаковые значения
              const compositeKey = `${opt.permalink}/${value.permalink}`;

              optionValuePermalinkToInfo[compositeKey] = {
                id: value.id,
                optionNameId: opt.id,
                valuePermalink: value.permalink
              };
            }
          });
        }
      }
    });
  }

  searchParams.forEach((value, key) => {
    if (key.startsWith('o-')) {
      const optionNameId = optionPermalinkToId[key];

      if (optionNameId) {
        const valuePermalinks = value.split(',');
        const valueIds = [];

        valuePermalinks.forEach((valuePermalink) => {
          const trimmed = valuePermalink.trim();

          // Используем составной ключ: optionPermalink/valuePermalink
          const compositeKey = `${key}/${trimmed}`;
          const info = optionValuePermalinkToInfo[compositeKey];

          if (info) {
            valueIds.push(info.id);
          }
        });

        if (valueIds.length > 0) {
          selectedOptionValueIdsByOptionNameId[optionNameId] = valueIds;
        }
      }
    } else if (!systemKeys.has(key)) {
      const propertyId = propertyPermalinkToId[key];

      if (propertyId) {
        const charPermalinks = value.split(',');

        charPermalinks.forEach((charPermalink) => {
          const info = characteristicPermalinkToInfo[charPermalink.trim()];
          if (info && !selectedCharacteristicIds.includes(info.id)) {
            selectedCharacteristicIds.push(info.id);
          }
        });
      }
    }
  });

  return { selectedCharacteristicIds, selectedOptionValueIdsByOptionNameId };
}

/**
 * Возвращает текущее состояние фильтров из URL (приоритет) или JSON
 */
export function getCurrentState(json) {
  const searchParams = new URLSearchParams(window.location.search);
  const urlFilters = parseHumanReadableFiltersFromUrl(json);

  // Если есть фильтры в URL - используем ТОЛЬКО их (URL = источник правды)
  const hasUrlFilters = urlFilters.selectedCharacteristicIds.length > 0 ||
                        Object.keys(urlFilters.selectedOptionValueIdsByOptionNameId).length > 0;

  // Проверяем явную очистку: есть другие параметры (price/order/only_available), но нет фильтров
  const hasOtherParams = searchParams.has('price_min') ||
                         searchParams.has('price_max') ||
                         searchParams.has('only_available') ||
                         searchParams.has('order') ||
                         searchParams.has('page');

  const wasExplicitlyCleared = !hasUrlFilters && hasOtherParams;

  if (hasUrlFilters || wasExplicitlyCleared) {
    // Читаем цены из URL, если они там есть
    // Если price_min/price_max отсутствуют в URL, это означает, что цена в крайнем положении
    const priceMinFromUrl = searchParams.get('price_min');
    const priceMaxFromUrl = searchParams.get('price_max');

    // Если параметры отсутствуют в URL, используем крайние значения из JSON (цена в крайнем положении)
    // Это нужно, чтобы правильно определить, что цена в крайнем положении при построении URL
    const priceMin = priceMinFromUrl !== null ? priceMinFromUrl : (json && json.products_price_min);
    const priceMax = priceMaxFromUrl !== null ? priceMaxFromUrl : (json && json.products_price_max);
    const onlyAvailable = searchParams.get('only_available') === 'true';
    const order = searchParams.get('order');

    return {
      selectedCharacteristicIds: urlFilters.selectedCharacteristicIds,
      selectedOptionValueIdsByOptionNameId: urlFilters.selectedOptionValueIdsByOptionNameId,
      priceMin,
      priceMax,
      onlyAvailable,
      order
    };
  }

  // Если фильтров в URL нет И нет других параметров - берём из JSON (initial state)
  const selectedCharacteristicIds = [];
  const selectedOptionValueIdsByOptionNameId = {};

  if (json) {
    if (Array.isArray(json.properties)) {
      json.properties.forEach((prop) => {
        if (!Array.isArray(prop && prop.characteristics)) return;

        prop.characteristics.forEach((char) => {
          if (!char) return;
          const isSelected = char['current?'] || char.selected;
          if (isSelected && char.id) {
            selectedCharacteristicIds.push(char.id);
          }
        });
      });
    }

    if (Array.isArray(json.options)) {
      json.options.forEach((opt) => {
        if (!Array.isArray(opt && opt.values) || !opt.id) return;

        opt.values.forEach((value) => {
          if (!value) return;
          const isSelected = value['current?'] || value.selected;
          if (isSelected && value.id) {
            if (!selectedOptionValueIdsByOptionNameId[opt.id]) {
              selectedOptionValueIdsByOptionNameId[opt.id] = [];
            }
            selectedOptionValueIdsByOptionNameId[opt.id].push(value.id);
          }
        });
      });
    }
  }

  const priceMin = searchParams.get('price_min') || (json && json.current_price_min);
  const priceMax = searchParams.get('price_max') || (json && json.current_price_max);
  const onlyAvailable = searchParams.get('only_available') === 'true';
  const order = searchParams.get('order');

  return {
    selectedCharacteristicIds,
    selectedOptionValueIdsByOptionNameId,
    priceMin,
    priceMax,
    onlyAvailable,
    order
  };
}

/**
 * Применяет состояние DOM формы к объекту состояния фильтров
 */
export function applyDomStateToState(formNode, state) {
  if (!formNode || !state) return;

  const checkedCharacteristics = formNode.querySelectorAll("input[name='characteristics[]']:checked");
  const domCharacteristicIds = Array.from(checkedCharacteristics)
    .map((el) => parseInt(el.value, 10))
    .filter((id) => !isNaN(id));

  const checkedOptions = formNode.querySelectorAll("input[type='checkbox'][name^='options[']:checked");
  const selectedOptionValueIdsByOptionNameId = {};

  Array.from(checkedOptions).forEach((el) => {
    const name = el.name;
    const match = name && name.match(/^options\[(\d+)\]/);
    if (!match) return;

    const optionNameId = match[1];
    const valueId = parseInt(el.value, 10);
    if (isNaN(valueId)) return;

    if (!selectedOptionValueIdsByOptionNameId[optionNameId]) {
      selectedOptionValueIdsByOptionNameId[optionNameId] = [];
    }
    selectedOptionValueIdsByOptionNameId[optionNameId].push(valueId);
  });

  state.selectedCharacteristicIds = domCharacteristicIds;
  state.selectedOptionValueIdsByOptionNameId = selectedOptionValueIdsByOptionNameId;

  const numericProperties = {};
  const PROPERTY_GT_REGEX = /^properties_gt\[(\d+)\]/;
  const PROPERTY_LT_REGEX = /^properties_lt\[(\d+)\]/;

  const processNumericPropertyInputs = (inputs, regex, fieldName) => {
    Array.from(inputs).forEach((el) => {
      const match = el.name && el.name.match(regex);
      if (!match) return;

      const propertyId = parseInt(match[1], 10);
      if (isNaN(propertyId)) return;

      if (!numericProperties[propertyId]) {
        numericProperties[propertyId] = {};
      }
      numericProperties[propertyId][fieldName] = el.value || null;
    });
  };

  const propertiesGtInputs = formNode.querySelectorAll("input[name^='properties_gt[']");
  const propertiesLtInputs = formNode.querySelectorAll("input[name^='properties_lt[']");

  processNumericPropertyInputs(propertiesGtInputs, PROPERTY_GT_REGEX, 'gt');
  processNumericPropertyInputs(propertiesLtInputs, PROPERTY_LT_REGEX, 'lt');

  state.numericProperties = numericProperties;

  const priceMinInput = formNode.querySelector('input[name="price_min"]');
  const priceMaxInput = formNode.querySelector('input[name="price_max"]');
  const onlyAvailableInput = formNode.querySelector('input[name="only_available"]');

  if (priceMinInput) {
    state.priceMin = priceMinInput.value || null;
  }
  if (priceMaxInput) {
    state.priceMax = priceMaxInput.value || null;
  }
  if (onlyAvailableInput) {
    state.onlyAvailable = onlyAvailableInput.checked ? 'true' : null;
  }
}

/**
 * Собирает человекочитаемый URL коллекции на основе состояния фильтра
 */
export function buildHumanReadableUrl(state, permalinkMaps, json) {
  const basePath = window.location.pathname.split('?')[0].split('#')[0];
  const humanReadablePairs = [];

  if (Array.isArray(state.selectedCharacteristicIds) && state.selectedCharacteristicIds.length) {
    const charIdToPropertyPermalink = new Map();
    if (Array.isArray(json && json.properties)) {
      json.properties.forEach((prop) => {
        if (!Array.isArray(prop && prop.characteristics) || !prop.permalink) return;
        prop.characteristics.forEach((char) => {
          if (char && char.id) {
            charIdToPropertyPermalink.set(char.id, prop.permalink);
          }
        });
      });
    }

    const charsByPropertyPermalink = {};

    state.selectedCharacteristicIds.forEach((charId) => {
      const charPermalink = permalinkMaps.characteristicIdToPermalink && permalinkMaps.characteristicIdToPermalink[charId];
      if (!charPermalink) return;

      const propPermalink = charIdToPropertyPermalink.get(charId);
      if (!propPermalink) return;

      if (!charsByPropertyPermalink[propPermalink]) {
        charsByPropertyPermalink[propPermalink] = [];
      }
      charsByPropertyPermalink[propPermalink].push(charPermalink);
    });

    Object.keys(charsByPropertyPermalink).forEach((propPermalink) => {
      const values = charsByPropertyPermalink[propPermalink];
      if (!values.length) return;
      values.sort();

      const encodedKey = encodeURIComponent(propPermalink);
      const encodedValues = values.map((v) => encodeURIComponent(v)).join(',');

      humanReadablePairs.push(`${encodedKey}=${encodedValues}`);
    });
  }

  if (state.selectedOptionValueIdsByOptionNameId) {
    Object.keys(state.selectedOptionValueIdsByOptionNameId).forEach((optionNameIdStr) => {
      const optionNameId = parseInt(optionNameIdStr, 10);
      const optionNamePermalink = permalinkMaps.optionNameIdToPermalink && permalinkMaps.optionNameIdToPermalink[optionNameId];
      const valueIds = state.selectedOptionValueIdsByOptionNameId[optionNameIdStr];

      if (!optionNamePermalink || !Array.isArray(valueIds) || !valueIds.length) {
        return;
      }

      const valuePermalinks = valueIds
        .map((valueId) => permalinkMaps.optionValueIdToPermalink && permalinkMaps.optionValueIdToPermalink[valueId])
        .filter(Boolean);

      if (!valuePermalinks.length) return;

      valuePermalinks.sort();

      const encodedKey = encodeURIComponent(optionNamePermalink);
      const encodedValues = valuePermalinks.map((v) => encodeURIComponent(v)).join(',');

      humanReadablePairs.push(`${encodedKey}=${encodedValues}`);
    });
  }

  if (state.numericProperties && Object.keys(state.numericProperties).length) {
    const numericPropertyIdToPermalink = new Map();
    const numericPropertyIdToDefaults = new Map();

    if (Array.isArray(json && json.properties)) {
      json.properties.forEach((prop) => {
        if (prop && prop.id && prop.permalink && prop['is_numeric?']) {
          numericPropertyIdToPermalink.set(prop.id, prop.permalink);

          if (Array.isArray(prop.characteristics) && prop.characteristics.length > 0) {
            const chars = prop.characteristics
              .map((char) => parseFloat(char && char.title))
              .filter((n) => !isNaN(n));

            if (chars.length > 0) {
              const defaultMin = Math.min(...chars);
              const defaultMax = Math.max(...chars);
              numericPropertyIdToDefaults.set(prop.id, { min: defaultMin, max: defaultMax });
            }
          }
        }
      });
    }

    Object.keys(state.numericProperties).forEach((propertyIdStr) => {
      const propertyId = parseInt(propertyIdStr, 10);
      if (isNaN(propertyId)) return;

      const propPermalink = numericPropertyIdToPermalink.get(propertyId);
      if (!propPermalink) return;

      const range = state.numericProperties[propertyIdStr];
      const minValue = range.gt ? parseFloat(range.gt) : null;
      const maxValue = range.lt ? parseFloat(range.lt) : null;

      const defaults = numericPropertyIdToDefaults.get(propertyId);
      if (!defaults) return;

      const isMinChanged = minValue !== null && !isNaN(minValue) && minValue !== defaults.min;
      const isMaxChanged = maxValue !== null && !isNaN(maxValue) && maxValue !== defaults.max;

      if (isMinChanged || isMaxChanged) {
        const encodedKey = encodeURIComponent(propPermalink);
        let encodedValues = '';
        if (isMinChanged && isMaxChanged) {
          encodedValues = `${encodeURIComponent(range.gt)},${encodeURIComponent(range.lt)}`;
        } else if (isMinChanged) {
          encodedValues = `${encodeURIComponent(range.gt)},`;
        } else if (isMaxChanged) {
          encodedValues = `,${encodeURIComponent(range.lt)}`;
        }
        humanReadablePairs.push(`${encodedKey}=${encodedValues}`);
      }
    });
  }

  const systemParams = new URLSearchParams();
  const currentParams = new URLSearchParams(window.location.search);
  const systemKeys = [
    'order',
    'page',
    'page_size',
    'q',
    'lang',
    'only_available',
    'filter',
    'collection'
  ];

  systemKeys.forEach((key) => {
    if (!currentParams.has(key)) {
      return;
    }

    currentParams.forEach((value, k) => {
      if (k === key || k.indexOf(key + '[') === 0) {
        if (!systemParams.has(k)) {
          systemParams.set(k, value);
        }
      }
    });
  });

  // Добавляем цену в URL только если она отличается от крайних значений
  if (state.priceMin != null) {
    const priceMinNum = parseFloat(state.priceMin);
    const defaultPriceMin = json && json.products_price_min ? parseFloat(json.products_price_min) : null;

    // Добавляем только если значение отличается от минимального
    if (defaultPriceMin === null || isNaN(priceMinNum) || priceMinNum !== defaultPriceMin) {
      systemParams.set('price_min', state.priceMin);
    }
  }
  if (state.priceMax != null) {
    const priceMaxNum = parseFloat(state.priceMax);
    const defaultPriceMax = json && json.products_price_max ? parseFloat(json.products_price_max) : null;

    // Добавляем только если значение отличается от максимального
    if (defaultPriceMax === null || isNaN(priceMaxNum) || priceMaxNum !== defaultPriceMax) {
      systemParams.set('price_max', state.priceMax);
    }
  }
  if (state.onlyAvailable === 'true') {
    systemParams.set('only_available', 'true');
  }
  if (state.order) {
    systemParams.set('order', state.order);
  }

  const systemQuery = systemParams.toString();
  const parts = [];

  if (humanReadablePairs.length) {
    parts.push(humanReadablePairs.join('&'));
  }
  if (systemQuery) {
    parts.push(systemQuery);
  }

  const query = parts.join('&');
  return query ? `${basePath}?${query}` : basePath;
}
